import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import Switch from '@material-ui/core/Switch';
import { enable as enableDarkMode, disable as disableDarkMode } from 'darkreader';
import { Mode, setThemeMode, toggleMatchBrowserTheme, toggleMode } from 'src/state/uipose/UiPose.slice';
import { AppState } from 'src/store';
import { container, labelRow } from './DarkMode.styles';

const DARK_MODE_MEDIA_QUERY = '(prefers-color-scheme: dark)';

type StateProps = {
  mode: Mode;
  matchBrowserTheme: boolean;
};

type DispatchProps = {
  toggle: () => void;
  toggleBrowserTheme: () => void;
  setMode: (mode: Mode) => void;
};

type EffectProps = StateProps & Pick<DispatchProps, 'setMode'>;
type DarkModeMenuItemProps = StateProps & Pick<DispatchProps, 'toggle'>;
type MatchBrowserThemeMenuItemProps = StateProps & Pick<DispatchProps, 'toggleBrowserTheme'>;

const mapStateToProps = (state: AppState): StateProps => {
  return {
    mode: state.uiPose.themeMode,
    matchBrowserTheme: state.uiPose.matchBrowserTheme,
  };
};

const mapDispatchToProps = {
  toggle: toggleMode,
  toggleBrowserTheme: toggleMatchBrowserTheme,
  setMode: setThemeMode,
};

const mapEffectDispatchToProps = {
  setMode: setThemeMode,
};

const applyDarkMode = (isDark: boolean) => {
  if (isDark) {
    enableDarkMode({
      brightness: 100,
      contrast: 95,
      sepia: 0,
      scrollbarColor: 'rgba(235, 235, 235, 0.5)',
    });
  } else {
    disableDarkMode();
  }
};

const DarkModeMenuItemBase = ({ mode, toggle }: DarkModeMenuItemProps) => {
  const isDarkMode = mode === 'dark';
  return (
    <div className={container}>
      <span className={labelRow}>
        <i className={`fa ${isDarkMode ? 'fa-moon' : 'fa-sun'}`} />
        <span>{isDarkMode ? 'Dark mode' : 'Light mode'}</span>
      </span>
      <Switch checked={isDarkMode} onChange={() => toggle()} />
    </div>
  );
};

const MatchBrowserThemeMenuItemBase = ({ matchBrowserTheme, toggleBrowserTheme }: MatchBrowserThemeMenuItemProps) => {
  return (
    <div className={container}>
      <span className={labelRow}>
        <i className="fa fa-desktop" />
        <span>Match Browser Theme</span>
      </span>
      <Switch checked={matchBrowserTheme} onChange={() => toggleBrowserTheme()} />
    </div>
  );
};

const DarkModeEffectBase = ({ mode, matchBrowserTheme, setMode }: EffectProps) => {
  useEffect(() => {
    applyDarkMode(mode === 'dark');
  }, [mode]);

  useEffect(() => {
    if (!matchBrowserTheme || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia(DARK_MODE_MEDIA_QUERY);
    const syncMode = (matches: boolean) => setMode(matches ? 'dark' : 'light');
    const handleChange = (event: MediaQueryListEvent) => syncMode(event.matches);

    syncMode(mediaQuery.matches);

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [matchBrowserTheme, setMode]);

  return null;
};

export const DarkModeMenuItem = connect(mapStateToProps, mapDispatchToProps)(DarkModeMenuItemBase);
export const MatchBrowserThemeMenuItem = connect(mapStateToProps, mapDispatchToProps)(MatchBrowserThemeMenuItemBase);
export const DarkModeEffect = connect(mapStateToProps, mapEffectDispatchToProps)(DarkModeEffectBase);
